import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { fileUrl } from '../../lib/api';
import { useGraphStore } from '../../store/graphStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Box, User, Palette, Mountain, Frame, Sun, Camera, Sparkles, Cpu, Loader2, CheckCircle2, AlertCircle, Droplets, LayoutGrid, Type, Clapperboard, Combine, Globe, UserRound, Shirt, Smile, PenLine } from 'lucide-react';

// The interface offers a quality tier, never a model name, because the model is
// resolved at render time from the tier, the length and whichever provider has
// credit. Workflows saved before tiers existed still carry the two old values,
// so they are folded onto the ends of the scale here rather than being migrated
// in the database, which would break anyone still on the previous build.
const LEGACY_TIERS = { 'veo3-fast': 'fast', veo3: 'premium' };
export const TIER_LABELS = { fast: 'Fast', standard: 'Standard', premium: 'Premium' };

export function TIER_VALUE(model) {
  const v = String(model || '').toLowerCase();
  if (LEGACY_TIERS[v]) return LEGACY_TIERS[v];
  return TIER_LABELS[v] ? v : 'fast';
}

export const KIND_META = {
  product:     { color: '#F5A623', label: 'Product',     Icon: Box },
  model:       { color: '#A78BFA', label: 'Stand-in',    Icon: User },
  influencer:  { color: '#EC4899', label: 'Influencer',  Icon: UserRound },
  style:       { color: '#22D3EE', label: 'Style',       Icon: Palette },
  environment: { color: '#34D399', label: 'Environment', Icon: Mountain },
  framing:     { color: '#94A3B8', label: 'Framing',     Icon: Frame },
  lighting:    { color: '#94A3B8', label: 'Lighting',    Icon: Sun },
  camera:      { color: '#94A3B8', label: 'Camera',      Icon: Camera },
  mood:        { color: '#94A3B8', label: 'Mood',        Icon: Sparkles },
  color:       { color: '#94A3B8', label: 'Colour',      Icon: Droplets },
  composition: { color: '#94A3B8', label: 'Composition', Icon: LayoutGrid },
  custom:      { color: '#94A3B8', label: 'Custom',      Icon: Type },
  outfit:      { color: '#A3E635', label: 'Outfit',      Icon: Shirt },
  expression:  { color: '#94A3B8', label: 'Expression',  Icon: Smile },
  generator:   { color: '#FFFFFF', label: 'The photo',   Icon: Cpu },
  video:       { color: '#A78BFA', label: 'Video',        Icon: Clapperboard },
  combine:     { color: '#F472B6', label: 'How they relate', Icon: Combine },
  website:     { color: '#38BDF8', label: 'Website',      Icon: Globe },
};

const handleStyle = (color) => ({
  width: 11, height: 11, background: '#0E0E10', border: `2px solid ${color}`,
});

function Shell({ color, children, selected, hasSource, hasTarget }) {
  return (
    <div
      className="relative w-[200px] rounded-xl border bg-[#1A1A1E] transition-shadow"
      style={{
        borderColor: selected ? color : '#2A2A30',
        boxShadow: selected ? `0 0 0 1px ${color}, 0 0 24px -6px ${color}88` : 'none',
      }}
    >
      {hasTarget && <Handle type="target" position={Position.Left} style={handleStyle(color)} />}
      {children}
      {hasSource && <Handle type="source" position={Position.Right} style={handleStyle(color)} />}
    </div>
  );
}

function Header({ kind }) {
  const m = KIND_META[kind] || KIND_META.product;
  const { Icon } = m;
  return (
    <div className="flex items-center gap-2 border-b border-[#2A2A30] px-3 py-2">
      <span className="flex h-5 w-5 items-center justify-center rounded" style={{ background: `${m.color}22` }}>
        <Icon size={13} style={{ color: m.color }} />
      </span>
      <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: m.color }}>{m.label}</span>
    </div>
  );
}

export function AssetNode({ data, selected }) {
  const m = KIND_META[data.kind] || KIND_META.product;
  // A model is a person, and a person can be given things: the wire into them
  // is what says whether the cap is worn or the jar is held. Products, styles
  // and environments take nothing, so they keep the one connector they had.
  return (
    <Shell color={m.color} selected={selected} hasSource hasTarget={data.kind === 'model'}>
      <Header kind={data.kind} />
      <div className="p-3">
        {data.thumb ? (
          <div className="mb-2 aspect-video w-full overflow-hidden rounded-md border border-[#2A2A30]">
            <img src={fileUrl(data.thumb)} alt="" className="h-full w-full object-cover" />
          </div>
        ) : data.kind === 'style' ? (
          <div className="mb-2 flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ background: data.accent || m.color }} />
            <span className="text-xs text-white/60">intensity {Math.round((data.intensity ?? 0.8) * 100)}%</span>
          </div>
        ) : null}
        <p className="truncate text-sm font-medium text-white">{data.name || m.label}</p>
      </div>
    </Shell>
  );
}

export function InfluencerNode({ data, selected }) {
  const m = KIND_META.influencer;
  // Wire a garment, a product or another person into this one and the wiring
  // itself says the relation: worn, held, or in the frame together.
  return (
    <Shell color={m.color} selected={selected} hasSource hasTarget>
      <Header kind="influencer" />
      <div className="p-3">
        {data.thumb ? (
          <div className="mb-2 aspect-square w-full overflow-hidden rounded-md border border-[#2A2A30]">
            <img src={fileUrl(data.thumb)} alt="" className="h-full w-full object-cover" />
          </div>
        ) : (
          <div className="mb-2 flex aspect-square w-full items-center justify-center rounded-md border border-dashed border-[#2A2A30] bg-[#0E0E10]">
            <UserRound size={20} className="text-white/25" />
          </div>
        )}
        <p className="truncate text-sm font-medium text-white">{data.name || 'Influencer'}</p>
        <p className="mt-0.5 text-[10px] text-white/40">consistent identity</p>
      </div>
    </Shell>
  );
}

export function ModifierNode({ data, selected, id }) {
  const m = KIND_META[data.kind] || KIND_META.framing;
  // Un preset de prompt de 600 palabras convertia el nodo en una sabana de
  // texto ilegible en medio del canvas. Custom y Outfit (los dos de texto
  // libre) enseñan ~2 lineas + contador y se editan en un dialogo comodo.
  if (data.kind === 'custom' || data.kind === 'outfit') {
    return <TextCardNode data={data} selected={selected} id={id} meta={m} />;
  }
  const summary = {
    framing: `${data.aspect || '4:5'} · ${data.shot || 'three-quarter'}`,
    lighting: data.preset || 'soft directional',
    camera: `${data.lens || 50}mm · ${data.aperture || 'f/2.0'}`,
    mood: data.preset || 'calm',
    color: `${data.preset || 'warm'} grade`,
    composition: data.preset || 'rule of thirds',
    expression: data.preset || 'warm genuine smile',
  }[data.kind];
  return (
    <Shell color={m.color} selected={selected} hasSource>
      <Header kind={data.kind} />
      <div className="p-3">
        <p className="text-sm text-white/80">{summary}</p>
      </div>
    </Shell>
  );
}

// Cuenta legible: '1.9k words' en vez de 1932.
function wordCount(text) {
  const n = (text || '').trim().split(/\s+/).filter(Boolean).length;
  if (!n) return null;
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k words` : `${n} word${n === 1 ? '' : 's'}`;
}

// Tarjeta colapsada para nodos de texto libre: titulo, 2 lineas con
// line-clamp, contador y un boton que abre el editor grande. El guardado va
// al mismo sitio del grafo (updateNodeData sobre data.text).
function TextCardNode({ data, selected, id, meta }) {
  const updateNodeData = useGraphStore((s) => s.updateNodeData);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const text = data.text || '';
  const count = wordCount(text);
  const placeholder = data.kind === 'outfit' ? 'describe the outfit…' : 'add your text…';

  const openEditor = () => { setDraft(text); setEditing(true); };
  const save = () => { updateNodeData(id, { text: draft }); setEditing(false); };

  return (
    <Shell color={meta.color} selected={selected} hasSource>
      <Header kind={data.kind} />
      <div className="p-3">
        <p className="line-clamp-2 text-xs leading-relaxed text-white/75">{text || placeholder}</p>
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[10px] text-white/40">{count || 'empty'}</span>
          <button
            onClick={openEditor}
            data-testid={`node-edit-prompt-${id}`}
            className="nodrag inline-flex items-center gap-1 rounded-full border border-[#2A2A30] px-2 py-0.5 text-[10px] text-white/70 transition-colors hover:border-white/30 hover:text-white"
          >
            <PenLine size={10} /> Edit prompt
          </button>
        </div>
      </div>
      <Dialog open={editing} onOpenChange={(o) => { if (!o) setEditing(false); }}>
        <DialogContent className="max-w-3xl border-[#2A2A30] bg-[#141417] text-white">
          <DialogHeader>
            <DialogTitle className="font-serif-display flex items-center gap-2 text-white">
              <meta.Icon size={16} style={{ color: meta.color }} /> {meta.label}
              {wordCount(draft) && <span className="text-xs font-normal text-white/40">· {wordCount(draft)}</span>}
            </DialogTitle>
          </DialogHeader>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            autoFocus
            data-testid="node-prompt-editor"
            className="min-h-[50vh] w-full resize-y rounded-md border border-[#2A2A30] bg-[#0E0E10] p-3 text-sm leading-relaxed text-white/90 focus:border-white/30 focus:outline-none"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)}
              className="rounded-full border border-[#2A2A30] px-4 py-2 text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white">
              Cancel
            </button>
            <button onClick={save} data-testid="node-prompt-save"
              className="rounded-full bg-white px-5 py-2 text-sm font-medium text-black transition-transform hover:scale-[1.02]">
              Save
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

export function GeneratorNode({ data, selected }) {
  const m = KIND_META.generator;
  const status = data.status || 'idle';
  return (
    <Shell color={m.color} selected={selected} hasTarget>
      <Header kind="generator" />
      <div className="p-3">
        <div className="mb-2 aspect-square w-full overflow-hidden rounded-md border border-[#2A2A30] bg-[#0E0E10]">
          {data.resultUrl ? (
            <img src={fileUrl(data.resultUrl)} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              {status === 'running' || status === 'queued' ? (
                <Loader2 size={22} className="animate-spin text-white/50" />
              ) : status === 'failed' ? (
                <AlertCircle size={22} className="text-red-400" />
              ) : (
                <Cpu size={22} className="text-white/25" />
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs capitalize text-white/60">{data.quality || 'standard'}</span>
          <span className="inline-flex items-center gap-1 text-[11px]">
            {status === 'succeeded' && <><CheckCircle2 size={12} className="text-node-environment" /> <span className="text-white/60">done</span></>}
            {(status === 'running' || status === 'queued') && (
              <span className="text-white/50">
                {data.progressTotal ? `step ${data.progressStep}/${data.progressTotal}` : `${status}…`}
              </span>
            )}
            {status === 'failed' && <span className="text-red-400">failed</span>}
            {status === 'idle' && <span className="text-white/40">idle</span>}
          </span>
        </div>
        {status === 'running' && data.progressTotal > 0 && (
          <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-node-model transition-all"
              style={{ width: `${Math.min(100, (data.progressStep / data.progressTotal) * 100)}%` }} />
          </div>
        )}
      </div>
    </Shell>
  );
}

export function GeneratorVideoNode({ data, selected }) {
  const m = KIND_META.video;
  const status = data.status || 'idle';
  return (
    <Shell color={m.color} selected={selected} hasTarget>
      <Header kind="video" />
      <div className="p-3">
        <div className="mb-2 aspect-[9/16] w-full overflow-hidden rounded-md border border-[#2A2A30] bg-[#0E0E10]">
          {data.resultVideoUrl ? (
            <video src={fileUrl(data.resultVideoUrl)} className="h-full w-full object-cover" muted loop playsInline
              onLoadedData={(e) => { const p = e.currentTarget.play(); if (p) p.catch(() => {}); }} />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              {status === 'running' || status === 'queued' ? (
                <Loader2 size={22} className="animate-spin text-white/50" />
              ) : status === 'failed' ? (
                <AlertCircle size={22} className="text-red-400" />
              ) : (
                <Clapperboard size={22} className="text-white/25" />
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/60">{TIER_LABELS[TIER_VALUE(data.model)]} · {data.duration || 8}s</span>
          <span className="inline-flex items-center gap-1 text-[11px]">
            {status === 'succeeded' && <><CheckCircle2 size={12} className="text-node-environment" /> <span className="text-white/60">done</span></>}
            {(status === 'running' || status === 'queued') && <span className="text-white/50">{status}…</span>}
            {status === 'failed' && <span className="text-red-400">failed</span>}
            {status === 'idle' && <span className="text-white/40">idle</span>}
          </span>
        </div>
      </div>
    </Shell>
  );
}

export function CombineNode({ data, selected }) {  const m = KIND_META.combine;
  return (
    <Shell color={m.color} selected={selected} hasSource hasTarget>
      <Header kind="combine" />
      <div className="p-3">
        <p className="line-clamp-4 text-xs leading-relaxed text-white/75">{data.relation || 'Describe how the items relate (keep it visible!)…'}</p>
        <p className="mt-2 text-[10px] text-white/40">Merge 2-4 items → one scene</p>
      </div>
    </Shell>
  );
}

export function WebsiteNode({ data, selected }) {
  const m = KIND_META.website;
  return (
    <Shell color={m.color} selected={selected} hasSource>
      <Header kind="website" />
      <div className="p-3">
        {data.thumb ? (
          <div className="mb-2 aspect-video w-full overflow-hidden rounded-md border border-[#2A2A30]">
            <img src={fileUrl(data.thumb)} alt="" className="h-full w-full object-cover object-top" />
          </div>
        ) : (
          <div className="mb-2 flex aspect-video w-full items-center justify-center rounded-md border border-dashed border-[#2A2A30] bg-[#0E0E10]">
            <Globe size={20} className="text-white/25" />
          </div>
        )}
        <p className="truncate text-sm font-medium text-white">{data.url || 'Paste a URL or upload'}</p>
        <p className="mt-1 text-[10px] text-white/40">{data.sceneMode === 'screen' ? 'On-screen showcase' : 'Influencer review'}</p>
      </div>
    </Shell>
  );
}

export const nodeTypes = {
  'asset.product': AssetNode,
  'asset.model': AssetNode,
  'asset.influencer': InfluencerNode,
  'asset.style': AssetNode,
  'asset.environment': AssetNode,
  'asset.website': WebsiteNode,
  'modifier.framing': ModifierNode,
  'modifier.lighting': ModifierNode,
  'modifier.camera': ModifierNode,
  'modifier.mood': ModifierNode,
  'modifier.color': ModifierNode,
  'modifier.composition': ModifierNode,
  'modifier.custom': ModifierNode,
  'modifier.outfit': ModifierNode,
  'modifier.expression': ModifierNode,
  'asset.outfit': AssetNode,
  'combine.scene': CombineNode,
  'generator.image': GeneratorNode,
  'generator.video': GeneratorVideoNode,
};

export const UGC_SCRIPTS = [
  { key: 'master', label: 'Master template, safest', text: 'BEAT 1, 0 to 3s, medium shot from chest height: the presenter holds <the product> up beside their face, turns it so the front faces camera, and reacts to it.\nBEAT 2, 3 to 6s, closer, slightly lower angle: the presenter gestures toward <the product> with the other hand, small natural movements, eyes returning to camera.\nBEAT 3, 6 to 10s, wider again, eye level: the presenter lowers <the product>, nods once, and gestures down toward the caption.\n\nHandheld UGC, real room, natural window light, honest skin texture, unhurried gestures. Keep the product unchanged from the reference. No on-screen text.' },
  { key: 'unboxing', label: 'Unboxing', text: 'hands reach into the already-open box and lift the product out, presenting it clearly to camera, handheld UGC unboxing style, natural motion' },
  { key: 'try-on', label: 'Try-on', text: 'the model tries on the product and turns to show it from a few angles, mirror-selfie UGC vibe, natural relaxed movement' },
  { key: 'review', label: 'Review', text: 'the person holds the product up to camera and speaks enthusiastically about it, casual talking-head UGC review, warm natural gestures' },
  { key: 'reveal', label: 'Product reveal', text: 'slow cinematic push-in on the product with a subtle rotation, dramatic premium reveal, soft studio light' },
  { key: 'testimonial', label: 'Testimonial', text: 'the person looks into the camera and gives a warm authentic testimonial with a subtle nod, cozy UGC lighting' },
  { key: 'website', label: 'Website review', text: 'the presenter points at the laptop screen showing the website and speaks enthusiastically about it, casual UGC talking-head review, natural gestures' },
  { key: 'book-review', label: 'Book review', text: 'the person holds the book up to camera, taps the cover while speaking warmly about the story, then flips through a few pages, cosy bookstagram UGC review, natural unhurried gestures' },
  { key: 'ereader', label: 'E-reader read', text: 'the person settles in with the e-reader, the screen clearly visible to camera, glances up with a small smile while reading, cosy natural window light, calm handheld UGC' },
  { key: 'haul', label: 'Haul', text: 'the person shows several items to camera one after another, quick playful pacing, casual UGC haul energy' },
  { key: 'demo', label: 'How-to demo', text: 'the person demonstrates step by step how the product is used, hands clearly in frame, instructional UGC framing' },
  { key: 'before-after', label: 'Before / after', text: 'the person gestures between the before and after states and reacts with satisfaction, casual UGC comparison' },
  { key: 'gift', label: 'Gift wrap', text: 'the person wraps the product as a gift with ribbon and paper in frame, warm festive UGC styling, soft light' },

  // Full-scene templates. Anything in [BRACKETS] is meant to be swapped before
  // generating; the wording around it is what holds the format together, so it
  // is deliberately left intact.
  { key: 'podcast', label: 'Podcast', text: 'Podcast-style viral video of [confident young host], sitting in a modern studio with microphone and headphones, speaking directly to camera, expressive hand gestures, soft cinematic lighting, professional podcast setup, engaging social media content, realistic talking scene, high quality' },
  { key: 'travel-vlog', label: 'Travel vlog', text: 'Create a hyper-realistic shot of a [female] host with [sun-kissed blonde] hair, wearing a [coral tank top and white shorts], standing in a [palm-lined beach] setting. They speak excitedly, saying, "[one full excited sentence]". No music, no cinematic black bars, no watermarks, no captions. Static medium shot with natural lighting and realistic voice audio.' },
  { key: 'reporter', label: 'Reporter interview', text: 'A real-world news-style scene set in [a busy street area], showing a field reporter holding a microphone and interviewing a person about [traffic conditions]. The reporter stands facing the interviewee, speaking professionally while holding the microphone toward them. The interviewee responds naturally with subtle gestures. The environment looks realistic and active, like a real location, but not overly crowded. Natural lighting, slight ambient background movement. The camera is steady like a professional TV recording, medium shot framing. No cinematic effects, no dramatic motion. Clean visuals, broadcast style.' },
  { key: 'food-review', label: 'Food review', text: 'A highly realistic vertical video based on the uploaded reference image. The subject from the reference image is seated inside a parked car, captured from a fixed camera position mounted on the dashboard, facing the subject. The subject is holding and reviewing [the food item], bringing it into frame naturally and taking a small bite. The subject looks toward the camera and says: "[the reaction line]" with natural lip sync, subtle facial expressions and realistic timing. Real-life lighting through the car windows, detailed realistic interior, accurate materials and reflections. Ultra-realistic quality, smooth textures, accurate physics, lifelike motion, proper depth of field, slight background blur, stable framing. No stylization, no distortion, pure photorealism.' },
  { key: 'dropship-ad', label: 'Dropshipping ad', text: 'Create a high-converting short-form video ad optimized for [TikTok] targeting [the audience]. HOOK (first 2 seconds): a scroll-stopping problem or curiosity hook around [the core problem]. VIDEO FLOW: scene 1 the pain point, frustration, struggle, unmet need; scene 2 the product introduction with fast zoom and dynamic motion; scene 3 the transformation showing a clear benefit. STYLE: high-energy, fast cuts, bold captions, [glow-up transition] visual effects. Include text overlays highlighting benefits, close-up product shots and a strong call to action.' },
  { key: 'landmark', label: 'Landmark history', text: 'An ultra-realistic video of an influencer standing near [the landmark], a famous historical landmark. Facing the camera and confidently explaining its history while subtly gesturing toward it. The influencer has [the appearance] and is wearing [the outfit], styled naturally to match the environment. They speak clearly and calmly, saying: "[the one-sentence fact]". The camera is fixed and stable, framed to keep both the influencer and the landmark in focus throughout. Shot on a high-end cinema camera, real-life lighting, smooth realistic textures, accurate real-world physics. Documentary-style influencer content, cinematic depth, natural colour grading, sharp detail.' },
];

export const MODIFIER_PALETTE = [
  { type: 'modifier.framing', label: 'Framing', kind: 'framing', data: { kind: 'framing', aspect: '4:5', shot: 'three-quarter', crop: 'waist-up' } },
  { type: 'modifier.lighting', label: 'Lighting', kind: 'lighting', data: { kind: 'lighting', preset: 'soft directional' } },
  { type: 'modifier.camera', label: 'Camera', kind: 'camera', data: { kind: 'camera', lens: 85, aperture: 'f/2.0', angle: 'eye-level' } },
  { type: 'modifier.mood', label: 'Mood', kind: 'mood', data: { kind: 'mood', preset: 'luxurious' } },
  { type: 'modifier.color', label: 'Colour', kind: 'color', data: { kind: 'color', preset: 'warm' } },
  { type: 'modifier.composition', label: 'Composition', kind: 'composition', data: { kind: 'composition', preset: 'rule of thirds' } },
  { type: 'modifier.expression', label: 'Expression', kind: 'expression', data: { kind: 'expression', preset: 'warm genuine smile' } },
  { type: 'modifier.custom', label: 'Custom text', kind: 'custom', data: { kind: 'custom', text: '' } },
];

// Concept subjects: quick text-only subjects so users can start without uploading assets
export const CONCEPT_SUBJECTS = [
  { type: 'asset.product', label: 'Product', kind: 'product', data: { kind: 'product', name: 'a minimalist white sneaker' } },
  { type: 'asset.model', label: 'Model', kind: 'model', data: { kind: 'model', name: 'a fashion model' } },
  { type: 'asset.environment', label: 'Environment', kind: 'environment', data: { kind: 'environment', name: 'a sunlit studio' } },
];
